import { supabase } from '../supabase';
import { ProcessedSurveyResults, RoleResults, SurveyAspects, SurveySpectrums } from './types';
import { debug, DebugArea } from '../debug';
import { safePropertyAccess } from '../type-fixes';

function calculateAverages(values: number[]): number {
  if (values.length === 0) return 0;
  const sum = values.reduce((acc, val) => acc + val, 0);
  return Number((sum / values.length).toFixed(2));
}

export async function processSurveyResults(surveyCode: string): Promise<ProcessedSurveyResults> {
  debug("SURVEY_PROCESSING" as DebugArea, 'Processing results for survey:', surveyCode);

  // Get all completed responses for this survey
  const { data: responses, error } = await supabase
    .from('survey_responses')
    .select(`
      role:roles(name, function, level),
      custom_role_name,
      role_index,
      avg_vision, avg_execution,
      avg_purpose, avg_profit,
      avg_adaptive, avg_planned,
      avg_disruptive, avg_measured,
      avg_open, avg_structured,
      avg_intuitive, avg_strategic,
      current_vision_execution,
      current_purpose_profit,
      current_adaptive_planned,
      current_disruptive_measured,
      current_open_structured,
      current_intuitive_strategic,
      aspirational_vision_execution,
      aspirational_purpose_profit,
      aspirational_adaptive_planned,
      aspirational_disruptive_measured,
      aspirational_open_structured,
      aspirational_intuitive_strategic,
      effectiveness_score
    `)
    .eq('survey_code', surveyCode)
    .eq('status', 'completed')
    .order('role_index', { ascending: true });

  if (error) throw error;

  debug("SURVEY_PROCESSING" as DebugArea, `Found ${responses.length} responses`);

  // Track role counts for unique naming
  const roleCounts = new Map<string, number>();

  // Process each response into individual role results
  const roleResults = responses.map(response => {
    // Fix the variable redeclaration issues by renaming the variables
    const roleNameValue = safePropertyAccess(response.role, 'name');
    const baseRoleName = roleNameValue === 'Other' && response.custom_role_name
      ? `Other - ${response.custom_role_name}`
      : roleNameValue || 'Unknown Role';

    // Get count for this role and increment
    const count = (roleCounts.get(baseRoleName) || 0) + 1;
    roleCounts.set(baseRoleName, count);

    // Create unique role name if there are multiple responses
    const roleName = count > 1 ? `${baseRoleName} (${count})` : baseRoleName;

    debug("SURVEY_PROCESSING" as DebugArea, 'Processing role:', {
      baseRoleName,
      count,
      roleName,
      roleIndex: response.role_index
    });

    return {
      role: baseRoleName,
      function: safePropertyAccess(response.role, 'function'),
      level: safePropertyAccess(response.role, 'level'),
      aspects: {
        Vision: response.avg_vision || 0,
        Execution: response.avg_execution || 0,
        Purpose: response.avg_purpose || 0,
        Profit: response.avg_profit || 0,
        Adaptive: response.avg_adaptive || 0,
        Planned: response.avg_planned || 0,
        Disruptive: response.avg_disruptive || 0,
        Measured: response.avg_measured || 0,
        Open: response.avg_open || 0,
        Structured: response.avg_structured || 0,
        Intuitive: response.avg_intuitive || 0,
        Strategic: response.avg_strategic || 0
      },
      spectrums: {
        current: {
          'Vision vs Execution': response.current_vision_execution || 0,
          'Purpose vs Profit': response.current_purpose_profit || 0,
          'Adaptive vs Planned': response.current_adaptive_planned || 0,
          'Disruptive vs Measured': response.current_disruptive_measured || 0,
          'Open vs Structured': response.current_open_structured || 0,
          'Intuitive vs Strategic': response.current_intuitive_strategic || 0
        },
        aspirational: {
          'Vision vs Execution': response.aspirational_vision_execution || 0,
          'Purpose vs Profit': response.aspirational_purpose_profit || 0,
          'Adaptive vs Planned': response.aspirational_adaptive_planned || 0,
          'Disruptive vs Measured': response.aspirational_disruptive_measured || 0,
          'Open vs Structured': response.aspirational_open_structured || 0,
          'Intuitive vs Strategic': response.aspirational_intuitive_strategic || 0
        }
      },
      effectiveness: response.effectiveness_score || 0
    };
  });

  debug("SURVEY_PROCESSING" as DebugArea, `Processed ${roleResults.length} responses with roles:`, 
    roleResults.map(r => r.role)
  );

  // Calculate overall averages
  const allRoleResults = roleResults.map(r => ({
    aspects: r.aspects,
    spectrums: r.spectrums,
    effectiveness: r.effectiveness
  }));

  return {
    survey_results: {
      roles: roleResults,
      averages: {
        aspects: calculateAspectAverages(allRoleResults),
        spectrums: {
          current: calculateSpectrumAverages(allRoleResults, 'current'),
          aspirational: calculateSpectrumAverages(allRoleResults, 'aspirational')
        },
        effectiveness: calculateAverages(allRoleResults.map(r => r.effectiveness))
      }
    }
  };
}

function calculateAspectAverages(roleResults: Array<{ aspects: SurveyAspects }>): SurveyAspects {
  return {
    Vision: calculateAverages(roleResults.map(r => r.aspects.Vision)),
    Execution: calculateAverages(roleResults.map(r => r.aspects.Execution)),
    Purpose: calculateAverages(roleResults.map(r => r.aspects.Purpose)),
    Profit: calculateAverages(roleResults.map(r => r.aspects.Profit)),
    Adaptive: calculateAverages(roleResults.map(r => r.aspects.Adaptive)),
    Planned: calculateAverages(roleResults.map(r => r.aspects.Planned)),
    Disruptive: calculateAverages(roleResults.map(r => r.aspects.Disruptive)),
    Measured: calculateAverages(roleResults.map(r => r.aspects.Measured)),
    Open: calculateAverages(roleResults.map(r => r.aspects.Open)),
    Structured: calculateAverages(roleResults.map(r => r.aspects.Structured)),
    Intuitive: calculateAverages(roleResults.map(r => r.aspects.Intuitive)),
    Strategic: calculateAverages(roleResults.map(r => r.aspects.Strategic))
  };
}

function calculateSpectrumAverages(roleResults: Array<{ spectrums: { current: SurveySpectrums; aspirational: SurveySpectrums } }>, type: 'current' | 'aspirational'): SurveySpectrums {
  return {
    'Vision vs Execution': calculateAverages(roleResults.map(r => r.spectrums[type]['Vision vs Execution'])),
    'Purpose vs Profit': calculateAverages(roleResults.map(r => r.spectrums[type]['Purpose vs Profit'])),
    'Adaptive vs Planned': calculateAverages(roleResults.map(r => r.spectrums[type]['Adaptive vs Planned'])),
    'Disruptive vs Measured': calculateAverages(roleResults.map(r => r.spectrums[type]['Disruptive vs Measured'])),
    'Open vs Structured': calculateAverages(roleResults.map(r => r.spectrums[type]['Open vs Structured'])),
    'Intuitive vs Strategic': calculateAverages(roleResults.map(r => r.spectrums[type]['Intuitive vs Strategic']))
  };
}